import { isDefined } from '@utils/isDefined';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';

import styles from './RecurringSubscriptionTermsEditor.module.scss';

import { Currency } from 'chaika-enums';

import { Checkbox } from '@components/Checkbox';
import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { translate } from '@chaika/i18n/helper';
import { Input, MoneyInput } from '@components/Input';
import { OptionType, Select } from '@components/Select';
import { Declension } from '@utils/Declension';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

interface Props {
    hasSpecialTerms?: boolean;
    needToDeleteSpecialTerms?: boolean;
    billingAmount?: number;
    billingAmountDuration?: string;
    disabled?: boolean;
    isUsedSpecialTerms: boolean;
    currency: Currency;
    onSpecialTermsChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onBillingAmountChange(billingAmount?: number): void;
    onBillingAmountDurationChange(duration?: string): void;
    onDeleteClick?(): void;
}

export const RecurringSubscriptionTermsEditor = observer(
    ({
        needToDeleteSpecialTerms,
        billingAmount,
        billingAmountDuration,
        hasSpecialTerms,
        disabled,
        currency,
        onDeleteClick,
        onBillingAmountChange,
        onSpecialTermsChange,
        onBillingAmountDurationChange,
        isUsedSpecialTerms,
    }: Props) => {
        const onMoneyChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                onBillingAmountChange(
                    isEmpty(event.currentTarget.value) ? undefined : Number(event.currentTarget.value) * 100,
                );
            },
            [onBillingAmountChange],
        );
        const onDurationChange = useCallback(
            (option: OptionType<string>) => {
                onBillingAmountDurationChange(option.value);
            },
            [onBillingAmountDurationChange],
        );

        const options: OptionType<string | 'infinitely'>[] = [
            { value: '30', label: `1 ${Declension.months(1)}` },
            { value: '90', label: `3 ${Declension.months(3)}` },
            { value: '180', label: `6 ${Declension.months(6)}` },
            { value: '360', label: `1 ${Declension.years(1)}` },
            { value: 'infinitely', label: translate('indefinitely') },
        ];

        useEffect(() => {
            if (!isUsedSpecialTerms) {
                onBillingAmountChange(undefined);
                onBillingAmountDurationChange(undefined);
            }
        }, [isUsedSpecialTerms, onBillingAmountChange, onBillingAmountDurationChange]);

        return (
            <div className={styles.root}>
                <div className={classNames([styles.checkboxWrapper, !isUsedSpecialTerms && styles.withoutMargin])}>
                    <Checkbox
                        isChecked={isUsedSpecialTerms}
                        onChange={onSpecialTermsChange}
                        label={translate('editSubscriptionTerms')}
                    />
                </div>
                {isUsedSpecialTerms && (
                    <>
                        <div className={styles.row}>
                            <div className={styles.wrapper}>
                                {needToDeleteSpecialTerms ? (
                                    <Input
                                        type={'string'}
                                        title={translate('costPerMonth')}
                                        dataTest={''}
                                        disabled={true}
                                        value={translate('costOfAdmission')}
                                        onChange={() => {}}
                                    />
                                ) : (
                                    <MoneyInput
                                        hideIfEmpty
                                        withoutKopecks
                                        title={translate('costPerMonth')}
                                        currency={currency}
                                        dataTest={''}
                                        disabled={disabled}
                                        value={isDefined(billingAmount) ? billingAmount / 100 : ''}
                                        onChange={onMoneyChange}
                                    />
                                )}
                            </div>
                            <div className={styles.wrapper}>
                                <Select
                                    options={options}
                                    title={translate('howLongIsPriceValid')}
                                    dataTest={''}
                                    disabled={needToDeleteSpecialTerms || disabled}
                                    selectedValue={needToDeleteSpecialTerms ? options[3].value : billingAmountDuration}
                                    onChange={onDurationChange}
                                />
                            </div>
                        </div>
                        {onDeleteClick && (
                            <div className={styles.button}>
                                <Button
                                    disabled={needToDeleteSpecialTerms || !hasSpecialTerms}
                                    dataTest={''}
                                    onClick={onDeleteClick}
                                    modifiers={[ButtonModifiers.Small]}
                                    theme={ButtonTheme.Secondary}
                                    label={translate('deleteSpecialTerms')}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    },
);
